import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import { useGetRequest } from '../../hooks/useGetRequest/useGetRequest';
import { getEvaluacionByIdAPI } from '../../consumers/backendApisUrls';
import { responseCode200 } from '../../consumers/httpRequiestsUtils';
import SpinnerLoading from '../../components/pageComponents/SpinnerLoading';
import './ModalVistaPrevia.css';

const ModalVistaPrevia = ({ showModal, onClose, evaluacionId }) => {
  const [evaluacionDetalle, setEvaluacionDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executeGet] = useGetRequest();

  // Constantes para mapeo (simulado, idealmente vendrían de props o context)
  const TIPOS_CARGO = {
    1: 'Docente',
    2: 'Asistente',
    3: 'Administrativo' 
  };
  
  const TIPOS_EVALUACION = {
    1: 'Conocimientos',
    2: 'Psicotécnico',
    3: 'Psicológico'
  };

  useEffect(() => {
    if (evaluacionId && showModal) {
      obtenerDetalleEvaluacion();
    }
  }, [evaluacionId, showModal]);

  const getPreguntaTexto = (pregunta) => pregunta?.textoPregunta || pregunta?.pregunta || '';
  const getPreguntaOpciones = (pregunta) => pregunta?.opciones || pregunta?.opcionesRespuesta || [];
  const getOpcionTexto = (opcion) => opcion?.textoOpcion || opcion?.texto || '';
  const getOpcionPuntaje = (opcion) => opcion?.puntaje || opcion?.valor || 0;
  const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('script, iframe, object, embed, style').forEach((node) => node.remove());
    doc.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = attr.value;
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if ((name === 'href' || name === 'src') && /^javascript:/i.test(value || '')) {
          el.removeAttribute(attr.name);
        }
      });
    });

    return doc.body.innerHTML;
  };
  const renderRichContent = (rawHtml) => {
    const safeHtml = sanitizeHtml(rawHtml);
    if (!safeHtml) return 'Sin contenido';
    return parse(safeHtml);
  };
  const resolveTipoEvaluacionLabel = (detalle) => {
    const rawText = `${detalle?.tipoEvaluacion || ''}`.toLowerCase();
    const idTipo = Number(detalle?.idTipoEvaluacion || detalle?.idTipoEval || 0);
    const idSubtipoPsico = Number(detalle?.idSubtipoPruebaPsicologica || 0);

    if (rawText.includes('psico')) return 'Psicológica';
    if (rawText.includes('conoc')) return 'Conocimientos';

    // Mapeo solicitado para códigos tipo 248801/24801
    if (idTipo === 248801 || idTipo === 24801 || idSubtipoPsico > 0) return 'Psicológica';
    if (idTipo === 248802 || idTipo === 24802) return 'Conocimientos';

    if (TIPOS_EVALUACION[idTipo]) return TIPOS_EVALUACION[idTipo];
    return `Tipo ${idTipo || '-'}`;
  };

  const obtenerDetalleEvaluacion = async () => {
    if (!evaluacionId) {
      setError('ID de evaluación no válido');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await executeGet(getEvaluacionByIdAPI(evaluacionId));

      if (responseCode200(response)) {
        let evaluacionApi = null;
        if (response?.data?.Data) {
          evaluacionApi = response.data.Data;
        } else if (response?.data?.data?.Data) {
          evaluacionApi = response.data.data.Data;
        } else if (response?.data?.data) {
          evaluacionApi = response.data.data;
        } else if (response?.data) {
          evaluacionApi = response.data;
        }

        if (evaluacionApi) {
          setEvaluacionDetalle(evaluacionApi);
        } else {
          setError('No se encontraron detalles para esta evaluación');
        }
      } else {
        setError('Error al cargar los detalles de la evaluación');
      }
    } catch (err) {
      console.error(err);
      setError('Error al cargar los detalles de la evaluación');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEvaluacionDetalle(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  const datosCalculados = useMemo(() => {
    if (!evaluacionDetalle) return null;

    const preguntas = evaluacionDetalle.preguntas || evaluacionDetalle.preguntasEvaluacion || [];
    const totalOpciones = preguntas.reduce((acc, p) => acc + getPreguntaOpciones(p).length, 0);

    const tipoEvaluacion = { 
      value: evaluacionDetalle.idTipoEvaluacion || evaluacionDetalle.idTipoEval,
      label: resolveTipoEvaluacionLabel(evaluacionDetalle)
    };
    
    const tipoCargo = { 
      value: evaluacionDetalle.idTipoCargo,
      label: evaluacionDetalle.tipoCargo || TIPOS_CARGO[evaluacionDetalle.idTipoCargo] || `Cargo ${evaluacionDetalle.idTipoCargo || '-'}`
    };
    
    const totalPreguntas = preguntas.length;
    const preguntasObligatorias = preguntas.filter((p) => p.esObligatoria).length;
    const puntajeAprobatorio =
      Number(evaluacionDetalle.puntajeAprobatorio || evaluacionDetalle.puntajeAprobacion || 0) || 0;
    const puntajeTotal =
      Number(evaluacionDetalle.puntajeTotal || evaluacionDetalle.puntajeMaximo || 0) || 0;
    const porcentajeAprobatorio = puntajeTotal > 0 ? ((puntajeAprobatorio / puntajeTotal) * 100).toFixed(1) : '0';
    
    return {
      tipoEvaluacion,
      tipoCargo,
      preguntas,
      totalPreguntas,
      preguntasObligatorias,
      totalOpciones,
      puntajeTotal,
      puntajeAprobatorio,
      porcentajeAprobatorio
    };
  }, [evaluacionDetalle]);

  if (!showModal) return null;

  return (
    <div className="vp-modal-overlay" role="dialog" aria-modal="true">
      <div className="vp-modal-panel">
        <div className="vp-modal-body">
          {loading ? (
            <div className="vp-loading-wrap">
              <SpinnerLoading />
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
              {error}
            </div>
          ) : evaluacionDetalle && datosCalculados ? (
            <>
              <section className="vp-section">
                <h6 className="vp-section-title">Resumen</h6>
                <div className="vp-grid">
                  <div><strong>ID Evaluación:</strong> {evaluacionDetalle.idEvaluacion || evaluacionId}</div>
                  <div><strong>Nombre:</strong> {evaluacionDetalle.nombreEvaluacion || '-'}</div>
                  <div><strong>Tipo evaluación:</strong> {datosCalculados.tipoEvaluacion.label}</div>
                  <div><strong>Tipo cargo:</strong> {datosCalculados.tipoCargo.label}</div>
                  <div><strong>Estado:</strong> {evaluacionDetalle.estado ? 'Activo' : 'Inactivo'}</div>
                  <div><strong>Tiempo límite:</strong> {evaluacionDetalle.tiempoLimite || evaluacionDetalle.tiempoLimiteMinutos || '-'} min</div>
                  <div><strong>Total preguntas:</strong> {datosCalculados.totalPreguntas}</div>
                </div>
              </section>

              <section className="vp-section">
                <h6 className="vp-section-title">Preguntas y Opciones</h6>
                {datosCalculados.preguntas.length === 0 ? (
                  <p className="text-muted mb-0">Sin preguntas en la respuesta del servicio.</p>
                ) : (
                  <div className="vp-question-list">
                    {datosCalculados.preguntas.map((pregunta, index) => (
                      <article key={pregunta.idEvaluacionPregunta || pregunta.id || index} className="vp-question-card">
                        <div className="vp-question-header">
                          <span><strong>#{pregunta.numero || index + 1}</strong></span>
                        </div>
                        <div className="vp-rich-text vp-question-text">{renderRichContent(getPreguntaTexto(pregunta))}</div>
                        <ul className="vp-option-list">
                          {getPreguntaOpciones(pregunta).map((opcion, opIndex) => (
                            <li key={opcion.idOpcion || opcion.idOpcionRespuesta || opIndex}>
                              <div className="vp-rich-text vp-option-text">{renderRichContent(getOpcionTexto(opcion))}</div>
                              <span className="vp-option-score">Puntaje: {getOpcionPuntaje(opcion)}</span>
                            </li>
                          ))}
                        </ul>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>

        <div className="vp-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalVistaPrevia;